import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Table } from "react-bootstrap";
import moment from "moment";
import { postOrdenCobro } from "../helpers/rutaOrdenesCobro";
import { generarPDFOrdenCobro } from "../helpers/pdfOrdenCobro";
import { getComandasPendientesByCliente } from "../helpers/rutaComandas";
import { generarPDFSaldoCliente } from "../helpers/pdfSaldoCliente"; // <<-- NUEVO

export default function ModalOrdenCobro({ show, onHide, cliente, comandasPreseleccionadas = [] }) {
  const [pendientes, setPendientes] = useState([]);
  const [seleccion, setSeleccion] = useState([]); // [{_id, codcom, nroviaje, fecha, saldo, total}]
  const [nroOrdenCliente, setNroOrdenCliente] = useState("");
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [observaciones, setObservaciones] = useState("");

  const [medios, setMedios] = useState([]);  // {tipo, banco, sucursal, cuenta, cbu, numero, fecha, vto, importe}
  const [rets, setRets] = useState([]);      // {tipo, numero, fecha, importe}

  // ---- totales
  const totalAplic = useMemo(
    () => seleccion.reduce((a, c) => a + Number((c.saldo ?? c.total) || 0), 0),
    [seleccion]
  );
  const totalMedios = useMemo(() => medios.reduce((a, m) => a + Number(m.importe || 0), 0), [medios]);
  const totalRets   = useMemo(() => rets.reduce((a, r) => a + Number(r.importe || 0), 0), [rets]);
  const diff = useMemo(() => (totalMedios + totalRets - totalAplic).toFixed(2), [totalMedios, totalRets, totalAplic]);

  // ---- carga de pendientes SIEMPRE, y mergeo de preseleccionadas
  useEffect(() => {
    if (!show || !cliente?._id) return;

    (async () => {
      const list = await getComandasPendientesByCliente(cliente._id); // trae saldo por comanda
      // Ordeno por fecha asc
      const ordenadas = (list || []).slice().sort((a,b)=> new Date(a.fecha||0) - new Date(b.fecha||0));
      setPendientes(ordenadas);

      // construir selección inicial: las preseleccionadas + mantener si ya había algo
      const mapSel = new Map();
      const seed = [
        ...comandasPreseleccionadas.map(c => {
          const found = ordenadas.find(x => x._id === c._id);
          return {
            _id: c._id,
            codcom: found?.codcom ?? c.codcom,
            nroviaje: found?.nroviaje ?? c.nroviaje,
            fecha: found?.fecha ?? c.fecha,
            saldo: Number((found?.saldo ?? c.saldo ?? found?.total ?? c.total) || 0),
            total: Number((found?.total ?? c.total) || 0),
          };
        }),
        ...seleccion, // por si el usuario ya estaba tildando
      ];
      seed.forEach(x => mapSel.set(x._id, x));
      setSeleccion(Array.from(mapSel.values()).filter(x => (x.saldo ?? x.total) > 0.01));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, cliente?._id]);

  const toggleSeleccion = (c) => {
    setSeleccion(prev => {
      const exists = prev.find(x => x._id === c._id);
      if (exists) return prev.filter(x => x._id !== c._id);
      return [...prev, { ...c, saldo: Number(c.saldo ?? (c.total ?? 0)) }];
    });
  };

  const seleccionarTodas = () => {
    const habilitadas = pendientes
      .filter(p => Number(p.saldo || 0) > 0.01)
      .map(p => ({ ...p, saldo: Number((p.saldo ?? p.total) || 0) }));
    setSeleccion(habilitadas);
  };

  const deseleccionarTodas = () => setSeleccion([]);

  const addMedio = (m) => setMedios(prev => [...prev, m]);
  const addRet   = (r) => setRets(prev => [...prev, r]);
  const removeMedio = (i) => setMedios(prev => prev.filter((_, idx) => idx !== i));
  const removeRet   = (i) => setRets(prev => prev.filter((_, idx) => idx !== i));

  const guardar = async () => {
    if (!nroOrdenCliente) return alert("Ingresá el número de orden del cliente.");
    if (!seleccion.length) return alert("Seleccioná al menos una comanda.");
    if (Math.abs(Number(diff)) > 0.01) {
      return alert("Los totales no cierran (ΣMedios + ΣRetenciones = ΣAplicaciones).");
    }

    const payload = {
      cliente: cliente._id,
      nroOrdenCliente,
      fecha,
      observaciones,
      aplicaciones: seleccion.map(c => ({
        comanda: c._id,
        importe: Number(c.saldo ?? c.total), // back valida contra saldo
      })),
      medios,
      retenciones: rets,
      totalAplicaciones: Number(totalAplic.toFixed(2)),
      totalMedios: Number(totalMedios.toFixed(2)),
      totalRetenciones: Number(totalRets.toFixed(2)),
    };

    const res = await postOrdenCobro(payload);
    if (res?.ok) {
      alert("Orden de cobro guardada correctamente.");
      try { generarPDFOrdenCobro(res.ordenCobro); } catch (e) { console.error("Error generando PDF:", e); }
      onHide(true); // cerrar y refrescar la tabla
    } else {
      console.error("OC error:", res);
      alert(res?.msg || "Error al guardar la orden.");
    }
  };

  // ---- Exportar SOLO las seleccionadas (las tildadas) ----
  const exportarSeleccionadasPDF = () => {
    if (!(seleccion?.length)) {
      alert("No hay comandas seleccionadas para exportar.");
      return;
    }

    const toNum  = v => Number(v || 0);
    const toDate = v => (v ? new Date(v) : null);
    const toStr  = v => (v == null ? "" : String(v));
    const pick = (obj, ...keys) => keys.find(k => obj?.[k] != null);

    const items = seleccion.map(c => {
      const numero   = toStr(c[pick(c,"codcom","numero","nrodecomanda","num")] || "");
      const nroviaje = toStr(c[pick(c,"nroviaje","nroViaje","nro_viaje","nro","nrovia","nro_via")] || "");
      const fecha    = toDate(c[pick(c,"fecha","fec","fech")] || null);
      const total    = toNum(c[pick(c,"total","Total")] || 0);
      const saldo    = toNum(c[pick(c,"saldo","Saldo")] || 0);
      const aplicado = Math.max(total - saldo, 0);
      return { numero, nroviaje, fecha, total, aplicado, saldo };
    });

    const totalSaldo = items.reduce((s,i) => s + i.saldo, 0);

    const clienteNombre =
      (cliente?.razonsocial) || (cliente?.nombre) || (cliente?.nombreFantasia) || "";
    const fechaCorte = toDate(fecha || new Date());

    generarPDFSaldoCliente({
      clienteNombre,
      fechaCorte,
      data: {
        totalSaldoComandas: totalSaldo,
        comandasConSaldo: items,
        ordenesCobroAbiertas: [],
      },
      titulo: "INFORME DE SALDOS PENDIENTES",
    });
  };

  // ---------- Exportar TODO lo que ves en la grilla ----------
  const exportarTodoPDF = () => {
    if (!(pendientes?.length)) {
      alert("No hay comandas pendientes para exportar.");
      return;
    }

    // helpers
    const pick = (obj, ...keys) => keys.find(k => obj?.[k] != null);
    const toNum  = v => Number(v || 0);
    const toDate = v => (v ? new Date(v) : null);
    const toStr  = v => (v == null ? "" : String(v));

    const items = pendientes.map(c => {
      const numero   = toStr(c[pick(c,"codcom","numero","nrodecomanda","num")] || "");
      const nroviaje = toStr(c[pick(c,"nroviaje","nroViaje","nro_viaje","nro","nrovia","nro_via")] || "");
      const fecha    = toDate(c[pick(c,"fecha","fec","fech")] || null);
      const total    = toNum(c[pick(c,"total","Total")] || 0);
      const saldo    = toNum(c[pick(c,"saldo","Saldo")] || 0);
      const aplicado = Math.max(total - saldo, 0);
      return { numero, nroviaje, fecha, total, aplicado, saldo };
    });

    const totalSaldo = items.reduce((s,i) => s + i.saldo, 0);

    const clienteNombre =
      (cliente?.razonsocial) || (cliente?.nombre) || (cliente?.nombreFantasia) || "";
    const fechaCorte = toDate(fecha || new Date());

    generarPDFSaldoCliente({
      clienteNombre,
      fechaCorte,
      data: {
        totalSaldoComandas: totalSaldo,
        comandasConSaldo: items,
        ordenesCobroAbiertas: [],
      },
      titulo: "INFORME DE SALDOS PENDIENTES",
    });
  };

  const $ = (n) => Number(n||0).toLocaleString("es-AR",{minimumFractionDigits:2, maximumFractionDigits:2});

  return (
    <Modal show={show} onHide={() => onHide(false)} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Nueva Orden de Cobro</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-2">
          <Col md={4}>
            <Form.Label>Cliente</Form.Label>
            <Form.Control value={cliente?.razonsocial || ""} readOnly />
          </Col>
          <Col md={4}>
            <Form.Label>N° Orden del Cliente</Form.Label>
            <Form.Control value={nroOrdenCliente} onChange={e => setNroOrdenCliente(e.target.value)} />
          </Col>
          <Col md={4}>
            <Form.Label>Fecha</Form.Label>
            <Form.Control type="date" value={fecha} onChange={e => setFecha(e.target.value)} />
          </Col>
        </Row>

        <Row className="align-items-center mb-2">
          <Col><b>Comandas pendientes</b></Col>
          <Col className="text-end">
            <Button size="sm" variant="outline-secondary" onClick={seleccionarTodas}>Tildar todas</Button>{" "}
            <Button size="sm" variant="outline-secondary" onClick={deseleccionarTodas}>Destildar todas</Button>{" "}
            <Button
              size="sm"
              variant="success"
              className="ms-1"
              onClick={exportarTodoPDF}
              style={{ minWidth: 150 }}
            >
              Exportar TODO PDF
            </Button>
            <Button
              size="sm"
              variant="outline-success"
              className="ms-1"
              onClick={exportarSeleccionadasPDF}
              disabled={!seleccion.length}
              style={{ minWidth: 190 }}
            >
              Exportar SELECCIONADAS PDF
            </Button>
            {Boolean(seleccion.length) && <span className="ms-2">Seleccionadas: <b>{seleccion.length}</b></span>}
          </Col>
        </Row>

        <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #e0e0e0", position: "relative" }}>
          <Table size="sm" bordered hover className="m-0">
            <thead>
              <tr style={{ position: "sticky", top: 0, background: "#f8f9fa", zIndex: 2 }}>
                <th style={{ width: 40, textAlign: "center" }}></th>
                <th>Comanda</th>
                <th>Viaje</th>
                <th>Fecha</th>
                <th>Factura</th>
                <th style={{ textAlign: "right" }}>Saldo</th>
                <th style={{ textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {pendientes.map((c) => {
                const checked = !!seleccion.find((x) => x._id === c._id);
                const disabled = Number(c.saldo || 0) <= 0.01;
                const inputId = `sel-${c._id}`;
                return (
                  <tr key={c._id} style={disabled ? { opacity: 0.5 } : undefined}>
                    <td style={{ width: 40, textAlign: "center" }}>
                      <input
                        id={inputId}
                        type="checkbox"
                        className="form-check-input m-0 p-0"
                        style={{ marginLeft: 0, position: "static" }}
                        disabled={disabled}
                        checked={checked}
                        onChange={() => toggleSeleccion(c)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>

                    <td>
                      <label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                        #{String(c.codcom).padStart(6, "0")}
                      </label>
                    </td>
                    <td>
                      <label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                        {c.nroviaje}
                      </label>
                    </td>
                    <td>
                      <label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                        {c.fecha ? moment(c.fecha).format("DD/MM/YYYY") : ""}
                      </label>
                    </td>

                    <td>
                      <label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                        {c.factura || ""}
                      </label>
                    </td>

                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                        ${$(c.saldo ?? 0)}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                        ${$(c.total ?? 0)}
                      </label>
                    </td>
                  </tr>
                );
              })}
              {!pendientes.length && (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    Sin pendientes para este cliente
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <Row className="mt-3">
          <Col md={6}>
            <h6>Medios de pago</h6>
            <Button size="sm" onClick={() => addMedio({ tipo: "transferencia", importe: 0, fecha })}>+ Transferencia</Button>{" "}
            <Button size="sm" onClick={() => addMedio({ tipo: "cheque_tercero", importe: 0 })}>+ Cheque tercero</Button>{" "}
            <Button size="sm" onClick={() => addMedio({ tipo: "cheque_propio", importe: 0 })}>+ Cheque propio</Button>{" "}
            <Button size="sm" onClick={() => addMedio({ tipo: "echeq", importe: 0 })}>+ E-cheq</Button>{" "}
            <Button size="sm" onClick={() => addMedio({ tipo: "efectivo", importe: 0 })}>+ Efectivo</Button>
            <Table size="sm" className="mt-2">
              <thead><tr><th>Tipo</th><th>Detalle</th><th>Importe</th><th></th></tr></thead>
              <tbody>
                {medios.map((m, i) => (
                  <tr key={i}>
                    <td>{m.tipo}</td>
                    <td>{[m.banco, m.numero, m.fecha && moment(m.fecha).format("DD/MM")].filter(Boolean).join(" • ")}</td>
                    <td style={{ textAlign: "right" }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={m.importe}
                        onChange={(e) => setMedios(prev => prev.map((x, idx) => idx === i ? { ...x, importe: Number(e.target.value) } : x))}
                      />
                    </td>
                    <td><Button variant="outline-danger" size="sm" onClick={() => removeMedio(i)}>x</Button></td>
                  </tr>
                ))}
                {!medios.length && <tr><td colSpan={4} className="text-muted">Agregá al menos un medio</td></tr>}
              </tbody>
            </Table>
          </Col>

          <Col md={6}>
            <h6>Retenciones</h6>
            <Button size="sm" onClick={() => addRet({ tipo: "iibb", importe: 0, fecha })}>+ IIBB</Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "ganancias", importe: 0, fecha })}>+ Ganancias</Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "iva", importe: 0, fecha })}>+ IVA</Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "suss", importe: 0, fecha })}>+ SUSS</Button>
            <Table size="sm" className="mt-2">
              <thead><tr><th>Tipo</th><th>N°</th><th>Fecha</th><th>Importe</th><th></th></tr></thead>
              <tbody>
                {rets.map((r, i) => (
                  <tr key={i}>
                    <td>{r.tipo}</td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={r.numero || ""}
                        onChange={(e) => setRets(prev => prev.map((x, idx) => idx === i ? { ...x, numero: e.target.value } : x))}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="date"
                        value={r.fecha || fecha}
                        onChange={(e) => setRets(prev => prev.map((x, idx) => idx === i ? { ...x, fecha: e.target.value } : x))}
                      />
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={r.importe}
                        onChange={(e) => setRets(prev => prev.map((x, idx) => idx === i ? { ...x, importe: Number(e.target.value) } : x))}
                      />
                    </td>
                    <td><Button variant="outline-danger" size="sm" onClick={() => removeRet(i)}>x</Button></td>
                  </tr>
                ))}
                {!rets.length && <tr><td colSpan={5} className="text-muted">Agregá retenciones si corresponde</td></tr>}
              </tbody>
            </Table>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col>
            <Form.Label>Observaciones</Form.Label>
            <Form.Control as="textarea" rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={4}><b>Total aplicaciones:</b> ${$(totalAplic)}</Col>
          <Col md={4}><b>Σ Medios:</b> ${$(totalMedios)}</Col>
          <Col md={4}><b>Σ Retenciones:</b> ${$(totalRets)}</Col>
        </Row>
        <Row>
          <Col md={12}>
            <b>Diferencia:</b>{" "}
            <span style={{color: Math.abs(Number(diff))<0.01 ? "green" : "red"}}>
              {diff}
            </span>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)}>Cancelar</Button>
        <Button
          variant="primary"
          onClick={guardar}
          disabled={Math.abs(Number(diff)) > 0.01 || !nroOrdenCliente || !seleccion.length}
        >
          Guardar y generar PDF
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
